import { ReactNode, useEffect, useMemo, useReducer, useRef } from 'react'
import {
  BoxConstraints,
  DrawOp,
  RenderBox,
  RenderBoxProvider,
  RenderTraceEvent,
  RequestLayoutProvider,
  Surface
} from '../rendering'

export class ViewportRenderBox extends RenderBox<{ width: number; height: number }> {
  performLayout(): void {
    this.box = { x: 0, y: 0, width: this.props.width, height: this.props.height }

    const tight = BoxConstraints.tight({
      width: this.props.width,
      height: this.props.height
    })

    for (const child of this.children) {
      child.performLayout(tight)
      child.box.x = 0
      child.box.y = 0
    }
  }

  draw(): DrawOp {
    return {
      type: 'group',
      id: this.id,
      bounds: this.box,
      children: this.children.map((c) => c.draw())
    }
  }
}

const NOOP = () => null

export function Viewport({
  children,
  width,
  height,
  Surface = NOOP,
  onTrace = NOOP,
  debug
}: {
  children: ReactNode
  width: number
  height: number
  Surface?: Surface
  onTrace?: (e: RenderTraceEvent) => void
  debug?: boolean
}) {
  const root = useRef(new ViewportRenderBox({ width, height })).current

  // Update the root props when width/height change
  useEffect(() => {
    root.props = { width, height }
    bumpGeneration()
  }, [root, width, height])

  /* trigger new layout when children change */
  const [gen, bumpGeneration] = useReducer((v: number) => v + 1, 0)

  return (
    <>
      <RenderBoxProvider value={root}>
        <RequestLayoutProvider value={bumpGeneration}>{children}</RequestLayoutProvider>
      </RenderBoxProvider>

      <SurfacerRenderer
        root={root}
        gen={gen}
        Surface={Surface}
        onTrace={onTrace}
        debug={debug}
        width={width}
        height={height}
      />
    </>
  )
}

function SurfacerRenderer({
  root,
  gen,
  Surface,
  onTrace,
  debug,
  width,
  height
}: {
  root: ViewportRenderBox
  gen: number
  Surface: Surface
  onTrace: (e: RenderTraceEvent) => void
  debug?: boolean
  width: number
  height: number
}) {
  const drawOps = useMemo(() => {
    root.performLayout()
    return root.draw()
  }, [root, gen])

  useEffect(() => {
    onTrace?.({ type: 'renderStep', step: 'build', data: drawOps })
  }, [drawOps, debug])

  return <Surface drawOp={drawOps} debug={debug} width={width} height={height} onTrace={onTrace} />
}
